/**
 * The hours learners can book (Nino, 3 Oct: one shared window, 07:00–23:00
 * in the learners' local time, so a facilitator can only claim times a
 * learner can book; Quality found a 22:45 session nobody could see).
 *
 * The server's value wins when it sends one (`bookable_window` on the
 * active-drills list, asked of Scoring); until then, the ruled hours in
 * the learners' zone (WIB, Asia/Jakarta, the zone Studio already shows
 * beside the facilitator's own).
 */
export interface BookableWindow {
  /** "07:00" */
  start: string;
  /** "23:00" */
  end: string;
  timezone: string;
}

export const RULED_WINDOW: BookableWindow = { start: '07:00', end: '23:00', timezone: 'Asia/Jakarta' };

let fromServer: BookableWindow | null = null;

export function setServerWindow(w: unknown): void {
  const x = w as Partial<BookableWindow> | null | undefined;
  fromServer = x && typeof x.start === 'string' && typeof x.end === 'string' && typeof x.timezone === 'string'
    ? { start: x.start, end: x.end, timezone: x.timezone }
    : null;
}

export function bookableWindow(): BookableWindow {
  return fromServer ?? RULED_WINDOW;
}

const toMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + (m || 0); };

// One formatter per zone: the date picker asks about every start of every day of a month.
const formatters = new Map<string, Intl.DateTimeFormat>();
const formatterFor = (tz: string) => {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    formatters.set(tz, f);
  }
  return f;
};

/** Wall-clock parts of an instant in a zone. */
function partsIn(at: Date, tz: string) {
  const p = formatterFor(tz)
    .formatToParts(at)
    .reduce<Record<string, string>>((acc, x) => { acc[x.type] = x.value; return acc; }, {});
  return { date: `${p.year}-${p.month}-${p.day}`, min: Number(p.hour) * 60 + Number(p.minute) };
}

/** Does [start, end] sit inside the window, on one day, in the window's zone? */
export function fitsWindow(start: Date, end: Date, w: BookableWindow = bookableWindow()): boolean {
  const s = partsIn(start, w.timezone);
  const e = partsIn(end, w.timezone);
  if (s.date !== e.date && !(e.min === 0 && toMin(w.end) >= 24 * 60)) return false;
  return s.min >= toMin(w.start) && e.min <= toMin(w.end) && e.min >= s.min;
}

/** The instant at which `hhmm` falls in `tz` on the same day (in `tz`) as `day`. */
export function windowEdge(day: Date, hhmm: string, tz: string): Date {
  const { date } = partsIn(day, tz);
  const guess = new Date(`${date}T${hhmm}:00Z`);
  const p = partsIn(guess, tz);
  const offsetMin = (Date.parse(`${p.date}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 60000 + p.min - toMin(hhmm);
  return new Date(guess.getTime() - offsetMin * 60000);
}

/** "07:00 – 23:00" in a zone, 24-hour in both languages (Nino, 12 Oct); never split across lines (Design, 28 Oct). */
export function windowRange(from: Date, to: Date, tz: string, locale: 'en' | 'id'): string {
  const f = (d: Date) => d.toLocaleTimeString(locale === 'id' ? 'id-ID' : 'en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  // A line may break after a dash even before a no-break space; the word joiner (U+2060) holds it.
  return `${f(from)}\u00a0–\u2060\u00a0${f(to)}`;
}
